import { makeRequest, makeRequestWithRetry, timeout } from 'src/utils/graphql';
import axios from 'axios';
import { ProductEntity, ProductVariantEntity, MaterialColorEntity } from 'src/modules/products';
import {
  QUERY_PRODUCT_DETAILS,
  QUERY_PRODUCT_RELATED,
  QUERY_PRODUCT_SERIES,
  QUERY_PRODUCT_VARIANTS,
} from '../queries';
import { ProductsDetailsActions } from './types';

const RETRY_TIMES = 10;
const RETRY_DELAY = 6000;

/** Fetch product details and assets */
export const fetchProductsDetailsData = async (slug: string) => {
  const response = await makeRequestWithRetry<{
    products: ProductEntity[];
  }>(
    QUERY_PRODUCT_DETAILS,
    {
      slug,
    },
    {
      errorId: 'fetchProductsDetailsData()',
    }
  );

  return response;
};

/**
 * Fetch related products and product series
 */
export const fetchRelatedProductsSSG = async (product: ProductEntity, dispatch: any) =>
  new Promise((resolve) => {
    if (!product) {
      console.error('fetchRelatedProductsSSG: empty product');
      resolve();
      return;
    }

    dispatch(fetchRelatedProductsDetailsBegin());

    const doFetchProductSeries = !!product?.product_series;
    const doFetchRelatedProducts = !!product?.product_series && product?.product_categories?.length;

    const numberOfFetches = (() => {
      if (doFetchRelatedProducts && doFetchProductSeries) return 2;
      if (doFetchRelatedProducts || doFetchProductSeries) return 1;

      console.error(`fetchRelatedProductsSSG: Nothing to fetch for product.id: ${product?.id}`);
      resolve();
      return 0;
    })();

    let fetchCount = 0;

    try {
      // Fetch product series
      if (doFetchProductSeries) {
        makeRequestWithRetry<{
          products: ProductEntity[];
        }>(
          QUERY_PRODUCT_SERIES,
          {
            productSeriesId: product.product_series.id,
            productId: product.id,
          },
          { errorId: 'fetchRelatedProductsSSG - QUERY_PRODUCT_SERIES' }
        ).then((response) => {
          dispatch(
            fetchRelatedProductsDetailsSuccess({
              productSeriesProducts: response.products,
            })
          );

          fetchCount++;
          if (fetchCount >= numberOfFetches) resolve();
        });
      }

      // Fetch related products from the same category
      if (doFetchRelatedProducts) {
        makeRequestWithRetry<{
          products: ProductEntity[];
        }>(
          QUERY_PRODUCT_RELATED,
          {
            productCategoryId: product.product_categories[0].id,
            productId: product.id,
            productSeriesId: product.product_series.id,
          },
          { errorId: 'fetchRelatedProductsSSG - QUERY_PRODUCT_RELATED' }
        ).then((response) => {
          dispatch(
            fetchRelatedProductsDetailsSuccess({
              relatedProducts: response.products,
            })
          );

          fetchCount++;
          if (fetchCount >= numberOfFetches) resolve();
        });
      }
    } catch (e) {
      console.error('fetchRelatedProductsSSG failed', e);
      dispatch(fetchRelatedProductsDetailsError({ message: e.message }));
      resolve();
    }
  });

// Fetch product variants on the client side
export const fetchVariantsProductsDetails = (product: ProductEntity) => {
  return async (dispatch) => {
    dispatch(fetchVariantsProductsDetailsBegin());

    try {
      if (!product?.product_variants?.length) {
        dispatch(
          fetchVariantsProductsDetailsSuccess({
            productVariants: [],
          })
        );
        return;
      }
      const dataResponse = await makeRequest<{
        productVariants: ProductVariantEntity[];
      }>(QUERY_PRODUCT_VARIANTS, {
        productVariants: product?.product_variants?.map((variant) => variant.id),
      });

      dispatch(
        fetchVariantsProductsDetailsSuccess({
          productVariants: dataResponse.productVariants,
        })
      );
    } catch (e) {
      console.error(e);
      dispatch(fetchVariantsProductsDetailsError({ message: e.message }));
    }
  };
};

// Fetch materials on the client side
export const fetchMaterialsProductsDetails = (product: ProductEntity) => {
  return async (dispatch) => {
    dispatch(fetchMaterialsProductsDetailsBegin());

    try {
      if (!product?.product_variants?.length) {
        dispatch(fetchMaterialsProductsDetailsSuccess([]));
        return;
      }

      const dataResponse = await axios.post(
        `${process.env.NEXT_PUBLIC_API_URL}/productVariantMaterials`,
        {
          where: {
            product_variants: product?.product_variants?.map((variant) => variant.id),
          },
        }
      );

      dispatch(fetchMaterialsProductsDetailsSuccess(dataResponse.data));
    } catch (e) {
      console.error(e);
      dispatch(fetchMaterialsProductsDetailsError({ message: e.message }));
    }
  };
};

export const fetchMaterialsProductsDetailsSSG = async (
  product: ProductEntity,
  __retryCount = 0
) => {
  try {
    const response = await axios.post(
      `${process.env.NEXT_PUBLIC_API_URL}/productVariantMaterials`,
      {
        where: {
          product_variants: product?.product_variants?.map((variant) => variant.id),
        },
      }
    );

    return response?.data;
  } catch (e) {
    if (__retryCount < RETRY_TIMES) {
      console.error(
        `fetchMaterialsProductsDetailsSSG failed [${__retryCount}]`,
        product?.name_en,
        product?.id
      );
      await timeout(RETRY_DELAY);
      return fetchMaterialsProductsDetailsSSG(product, __retryCount + 1);
    }
    console.error('fetchMaterialsProductsDetailsSSG failed', product?.name_en, product?.id, e);
    throw new Error('fetchMaterialsProductsDetailsSSG failed');
  }
};

/*
 * PRODUCT DETAILS ACTIONS
 */

export const fetchProductsDetailsDataBegin = () => ({
  type: ProductsDetailsActions.FetchDetailsBegin,
});

export const fetchProductsDetailsDataSuccess = (result: { product: ProductEntity }) => ({
  type: ProductsDetailsActions.FetchDetailsSuccess,
  payload: result,
});

export const fetchProductsDetailsDataError = (error) => ({
  type: ProductsDetailsActions.FetchDetailsError,
  payload: { error },
});

/*
 * RELATED PRODUCTS ACTIONS
 */

const fetchRelatedProductsDetailsBegin = () => ({
  type: ProductsDetailsActions.FetchRelatedProductsBegin,
});

const fetchRelatedProductsDetailsSuccess = (result: {
  productSeriesProducts?: ProductEntity[];
  relatedProducts?: ProductEntity[];
}) => ({
  type: ProductsDetailsActions.FetchRelatedProductsSuccess,
  payload: result,
});

const fetchRelatedProductsDetailsError = (error) => ({
  type: ProductsDetailsActions.FetchRelatedProductsError,
  payload: { error },
});

/*
 * PRODUCT VARIANTS ACTIONS
 */

const fetchVariantsProductsDetailsBegin = () => ({
  type: ProductsDetailsActions.FetchProductVariantsBegin,
});

const fetchVariantsProductsDetailsSuccess = (result: {
  productVariants: ProductVariantEntity[];
}) => ({
  type: ProductsDetailsActions.FetchProductVariantsSuccess,
  payload: result,
});

const fetchVariantsProductsDetailsError = (error) => ({
  type: ProductsDetailsActions.FetchProductVariantsError,
  payload: { error },
});

/*
 * MATERIAL ACTIONS
 */

export const fetchMaterialsProductsDetailsBegin = () => ({
  type: ProductsDetailsActions.FetchMaterialColorsBegin,
});

export const fetchMaterialsProductsDetailsSuccess = (result: MaterialColorEntity[]) => ({
  type: ProductsDetailsActions.FetchMaterialColorsSuccess,
  payload: result,
});

export const fetchMaterialsProductsDetailsError = (error) => ({
  type: ProductsDetailsActions.FetchMaterialColorsError,
  payload: { error },
});

/*
 * Displayed overlay
 */

export const displayProductsProperty = (propertyToDisplay) => ({
  type: ProductsDetailsActions.DisplayProductProperty,
  payload: { activeProductProperty: propertyToDisplay },
});
